<template>
    <div>
        <v-layout row align-center>
            <v-flex xs12>
                <v-tooltip bottom>
                    <template #activator="{on}">
                        <v-btn
                            small
                            color="secondary"
                            outline
                            block
                            class="ma-0"
                            v-on="on"
                            :disabled="!allowEdit || disabled || generating"
                            :loading="generating"
                            @click="generate"
                        >
                            <v-icon left small>cloud_download</v-icon>
                            {{ buttonTitle }}
                        </v-btn>
                    </template>
                    <span v-if="!allowEdit">Read Only</span>
                    <span v-else-if="disabled">{{ missingMessage }}</span>
                    <span v-else>Run report</span>
                </v-tooltip>
            </v-flex>
        </v-layout>

        <div v-if="missingLabels.length" class="pt-1 px-1">
            <div v-for="label in missingLabels" :key="label" class="caption error--text">
                {{ label }} is required
            </div>
        </div>

        <div v-if="generating" style="width: 100%" class="pa-2 text-xs-center">
            <v-progress-circular
                :size="40"
                :width="5"
                color="primary"
                indeterminate
            />
        </div>
        <div v-else-if="valueDocument" class="pa-1">
            <v-card class="pa-1">
                <app-document-container
                    :document="valueDocument"
                    :user-info="userInfo"
                />
            </v-card>
        </div>
    </div>
</template>

<script>
import {codeBaseMixin} from "../../../../../../codeBaseMixin";
import DocumentContainer from "../../../Documents/DocumentContainer";

// Keep in sync with the backend (beeswax.js MATTER_INPUTS).
const MATTER_INPUTS = {
    naturalPerson: [
        {key: 'firstName',      label: 'First Name',      required: true},
        {key: 'surname',        label: 'Surname',         required: true},
        {key: 'identityNumber', label: 'ID Number',       required: false},
        {key: 'passport',       label: 'Passport',        required: false},
    ],
    legalPerson: [
        {key: 'companyName',        label: 'Company Name',        required: true},
        {key: 'tradingName',        label: 'Trading Name',        required: false},
        {key: 'registrationNumber', label: 'Registration Number', required: true},
    ],
};

const NP_REPORTS = new Set(['cdd-report-natural-person','cdd-summary-report-natural-person','idv-report-natural-person']);

export default {
    mixins: [codeBaseMixin],
    components: {
        appDocumentContainer: DocumentContainer,
    },
    props: {
        stepID:        Number,
        userInfo:      Object,
        value:         Object,   // the field object (includes fieldID, value, lookup, editable, etc.)
        stepEditable:  Boolean,
        task:          Object,
    },
    data() {
        return {
            generating: false,
        };
    },
    computed: {
        config() {
            try {
                if (!this.value.lookup) return null;
                return typeof this.value.lookup === 'string' ? JSON.parse(this.value.lookup) : this.value.lookup;
            } catch (e) {
                return null;
            }
        },
        buttonTitle() {
            return (this.config && this.config.buttonTitle) || 'Generate Beeswax Report';
        },
        reportType() {
            return this.config && this.config.reportType;
        },
        matterType() {
            if (!this.reportType) return null;
            return NP_REPORTS.has(this.reportType) ? 'naturalPerson' : 'legalPerson';
        },
        inputSlots() {
            return this.matterType ? MATTER_INPUTS[this.matterType] : [];
        },
        // For each mapped input, read the *current* value of the referenced
        // workflow field so we can check which required ones are unset.
        mappedValues() {
            const out = {};
            if (!this.config || !this.config.inputMappings) return out;
            for (const slot of this.inputSlots) {
                const fieldId = this.config.inputMappings[slot.key];
                if (!fieldId) { out[slot.key] = null; continue; }
                const target = (this.task.structure.fields || []).find(f => f.fieldID === fieldId);
                const raw = target && target.value != null ? String(target.value).trim() : '';
                out[slot.key] = raw || null;
            }
            return out;
        },
        missingLabels() {
            return this.inputSlots
                .filter(slot => slot.required && !this.mappedValues[slot.key])
                .map(slot => slot.label);
        },
        disabled() {
            return !this.reportType || this.missingLabels.length > 0;
        },
        missingMessage() {
            if (!this.reportType) return 'Field not configured';
            if (this.missingLabels.length) return `${this.missingLabels.join(', ')} required`;
            return '';
        },
        allowEdit() {
            return this.stepEditable && this.value.editable == 1;
        },
        valueDocument() {
            try {
                const docId = parseInt(this.value.value, 10);
                if (!docId) return null;
                return (this.task.structure.documents || []).find(d => d.documentDBID == docId) || null;
            } catch (e) {
                return null;
            }
        },
    },
    methods: {
        generate() {
            if (!this.allowEdit || this.disabled || this.generating) return;
            this.generating = true;
            this.fetch({
                method: 'POST',
                url: `/beeswax/runReport/${this.userInfo.entityID}/${this.userInfo.userID}/${this.task.taskID}/${this.stepID}/${this.value.fieldID}`,
            })
                .then(data => {
                    if (data && data.doc) this.task.structure.documents.push(data.doc);
                    if (data && data.field) Object.assign(this.value, data.field);
                    this.value.saveStatus = 1;
                    const s = data && data.screening;
                    if (s && !s.complete) {
                        this.showSnack('Info', 'Report generated, but screening was still in progress — some checks may be missing. Re-run later for full results.', 'Close', 'orange');
                    } else if (s && s.caseStatus === 'NonCompliant') {
                        this.showSnack('Warning', `Screening result: Non-Compliant (Risk: ${s.riskStatus || 'Unknown'})`, 'Close', 'orange');
                    }
                })
                .catch(e => {
                    // eslint-disable-next-line
                    console.log(e);
                    this.showSnack('Error', 'Failed to generate Beeswax report. Please try again.', 'Close', 'red');
                })
                .finally(() => {
                    this.generating = false;
                });
        },
    },
};
</script>

<style scoped>
</style>
